import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Box, Button, Spinner } from 'theme-ui';
import { Inputform } from '../../forms-component/InputForm';
import FormSelect from '../../forms-component/globalSelect';
import { LuUpload, LuTrash2 } from 'react-icons/lu';
import { useDispatch, useSelector } from 'react-redux';
import { mediaUpload } from '../../../store/actions/media.action';
import { HistoryEntryModalProps } from './types';
import { caPhaseStatusOptions, caPhaseStampedAsOptions } from '../../../utils/constent';
import { RootState } from '../../../../@types';
import toast from 'react-hot-toast';
const HistoryEntryModal: React.FC<HistoryEntryModalProps> = ({
    isOpen,
    toggle,
    onSave,
    type,
    isEdit,
    selectedEntry,
    setSelectedEntry,
    modalHeader,
    titleHeader,
    isLoading,
    disableSave,
    isRestricted,
    entryIndex
}) => {
    const isMeeting = type === 'MEETING';
    const [descriptionError, setDescriptionError] = useState<string>('');
    const [dateError, setDateError] = useState('');
    const dispatch = useDispatch();
    const { uploadedMedia, loader: mediaLoader } = useSelector((state: RootState) => state?.media as any);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    useEffect(() => {
        if (!isOpen) {
            setIsDragOver(false);
            setDescriptionError('');
            setDateError('');
        }
    }, [isOpen]);

    const prevMediaLoader = useRef(mediaLoader);

    useEffect(() => {
        if (isOpen && selectedEntry?.status === 'NotCompleted') {
            setSelectedEntry(prev => prev ? { ...prev, status: 'Open' } : null);
        }
    }, [isOpen, selectedEntry?.status, setSelectedEntry]);

    useEffect(() => {
        if (isUploading && uploadedMedia) {
            const justFinished = prevMediaLoader.current === true && mediaLoader === false;
            if (isUploading && justFinished && uploadedMedia) {
                setSelectedEntry(prev => {
                    if (prev) {
                        const newAttachments = [...(prev.attachment || []), uploadedMedia];
                        return { ...prev, attachment: newAttachments };
                    }
                    return prev;
                });
                setIsUploading(false);
            }
        }
        prevMediaLoader.current = mediaLoader;
    }, [mediaLoader, uploadedMedia, isUploading, setSelectedEntry]);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = (event.target.files as FileList)[0];
        if (file) {
            if (file.size > 30 * 1024 * 1024) {
                toast.error('File size cannot exceed 30MB');
                return;
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'ca_phase');
            formData.append('uploadType', 'File');
            dispatch(mediaUpload(formData));
            setIsUploading(true);
        }
        // Clear the input value so the same file can be uploaded again if deleted
        event.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            if (file.size > 25 * 1024 * 1024) {
                toast.error('File size cannot exceed 30MB');
                return;
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'ca_phase');
            formData.append('uploadType', 'File');
            dispatch(mediaUpload(formData));
            setIsUploading(true);
        }
    };

    const removeAttachment = (indexToRemove: number) => {
        setSelectedEntry(prev => {
            if (prev && prev.attachment) {
                const newAttachments = [...prev.attachment];
                newAttachments.splice(indexToRemove, 1);
                return { ...prev, attachment: newAttachments };
            }
            return prev;
        });
    };

    const handleSave = () => {
        let hasError = false;

        if (isMeeting) {
            if (!selectedEntry?.description || selectedEntry.description.trim() === '') {
                setDescriptionError('Description is required');
                hasError = true;
            } else {
                setDescriptionError('');
            }

            if (!selectedEntry?.meetingDate || selectedEntry.meetingDate.toString().trim() === '') {
                setDateError('Date is required');
                hasError = true;
            } else {
                let yearStr = '';
                try {
                    const dateStr = selectedEntry.meetingDate instanceof Date
                        ? selectedEntry.meetingDate.toISOString().split('T')[0]
                        : selectedEntry.meetingDate.split('T')[0];
                    yearStr = dateStr.split('-')[0];
                } catch (e) {
                    yearStr = '';
                }
                if (!yearStr || yearStr.length !== 4 || parseInt(yearStr, 10) < 1900 || parseInt(yearStr, 10) > 2100) {
                    setDateError('Please enter a valid 4-digit year format (e.g. 2024)');
                    hasError = true;
                } else {
                    setDateError('');
                }
            }
        }

        if (hasError) return;

        if (onSave) onSave();
    };

    const closeButton = (
        <button
            type="button"
            aria-label="Close"
            onClick={!isLoading ? toggle : undefined}
            style={{
                color: titleHeader,
                background: 'transparent',
                border: 'none',
                fontSize: 23,
                lineHeight: 1,
                padding: 0,
                cursor: 'pointer'
            }}
        >
            <span aria-hidden="true">&times;</span>
        </button>
    );

    return (
        <Modal isOpen={isOpen} toggle={!isLoading ? toggle : undefined} backdrop={isLoading ? 'static' : true} keyboard={!isLoading} centered scrollable={true}>
            <ModalHeader toggle={!isLoading ? toggle : undefined} close={closeButton} style={{ backgroundColor: modalHeader, color: titleHeader }}>
                {isEdit ? `Edit ${isMeeting ? 'Meeting' : 'CA Phase'} ${entryIndex !== undefined ? `#${entryIndex + 1}` : 'Entry'}` : `New ${isMeeting ? 'Meeting' : 'CA Phase'} Entry`}
            </ModalHeader>
            <ModalBody style={{ backgroundColor: modalHeader, color: titleHeader, minHeight: isMeeting ? undefined : '650px' }}>
                {isMeeting ? (
                    <>
                        <Box mb={3}>
                            <Inputform
                                label="Description"
                                sx={{ color: titleHeader }}
                                type="text"
                                values={selectedEntry?.description || ''}
                                Name="description"
                                OnChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
                                    setSelectedEntry(prev => prev ? { ...prev, description: e.target.value } : null);
                                    if (e.target.value.trim() !== '') {
                                        setDescriptionError('');
                                    }
                                }}
                                Placeholder="Enter description"
                                asterisk={true}
                                errors={descriptionError}
                                touched={!!descriptionError}
                                disabled={isRestricted}
                            />
                        </Box>
                        <Box mb={3} sx={{ position: 'relative' }}>
                            <Inputform
                                inputClassName="custom-datepicker-input"
                                wrapperClassName="date-input-wrapper"
                                label="Date"
                                sx={{ color: titleHeader }}
                                type="date"
                                values={selectedEntry?.meetingDate ? (selectedEntry.meetingDate instanceof Date ? selectedEntry.meetingDate.toISOString().split('T')[0] : selectedEntry.meetingDate.split('T')[0]) : ''}
                                Name="meetingDate"
                                OnChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
                                    setSelectedEntry(prev => prev ? { ...prev, meetingDate: e.target.value } : null);
                                    if (e.target.value.trim() !== '') {
                                        setDateError('');
                                    }
                                }}
                                Placeholder="mm-dd-yyyy"
                                dateFormat="MM-dd-yyyy"
                                asterisk={true}
                                errors={dateError}
                                touched={!!dateError}
                                disabled={isRestricted}
                            />
                        </Box>
                        <Box mb={3}>
                            <Inputform
                                label="Meeting Recording"
                                sx={{ color: titleHeader }}
                                type="text"
                                values={selectedEntry?.meetingLink || ''}
                                Name="meetingLink"
                                OnChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setSelectedEntry(prev => prev ? { ...prev, meetingLink: e.target.value } : null)}
                                Placeholder="Enter link"
                                disabled={isRestricted}
                            />
                        </Box>
                        <Box mb={3}>
                            <Inputform
                                label="Meeting Notes"
                                sx={{ color: titleHeader }}
                                type="text"
                                values={selectedEntry?.meetingNotes || ''}
                                Name="meetingNotes"
                                OnChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setSelectedEntry(prev => prev ? { ...prev, meetingNotes: e.target.value } : null)}
                                Placeholder="Enter notes"
                                disabled={isRestricted}
                            />
                        </Box>
                    </>
                ) : (
                    <>
                        <Box mb={1}>
                            <Inputform
                                label="Subject"
                                sx={{ color: titleHeader }}
                                type="textarea"
                                values={selectedEntry?.cmt_subject || ''}
                                Name="cmt_subject"
                                OnChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setSelectedEntry(prev => prev ? { ...prev, cmt_subject: e.target.value } : null)}
                                disabled={isRestricted}
                                Placeholder="Enter subject"
                            />
                        </Box>
                        <Box mb={1}>
                            <FormSelect
                                label="Status"
                                sx={{ color: titleHeader }}
                                value={selectedEntry?.status || ''}
                                options={caPhaseStatusOptions}
                                onChange={(opt: { label: string; value: string }) => setSelectedEntry(prev => prev ? { ...prev, status: opt.value } : null)}
                            />
                        </Box>
                        <Box mb={2} sx={{ position: 'relative' }}>
                            <Inputform
                                inputClassName="custom-datepicker-input"
                                wrapperClassName="date-input-wrapper"
                                label="Closed Date"
                                sx={{ color: titleHeader }}
                                type="date"
                                values={selectedEntry?.closeDueDate ? selectedEntry?.closeDueDate.split('T')[0] : ''}
                                Name="closedDate"
                                OnChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
                                    setSelectedEntry(prev => prev ? { ...prev, closeDueDate: e.target.value } : null);
                                    if (e.target.value.trim() !== '') {
                                        setDateError('');
                                    }
                                }}
                                Placeholder="mm-dd-yyyy"
                                errors={dateError}
                                touched={!!dateError}
                            />
                        </Box>
                        <Box mb={3}>
                            <span style={{ fontSize: '14px', marginBottom: '8px', display: 'block', color: titleHeader, fontWeight: 500 }}>
                                Upload Document
                            </span>
                            <label htmlFor="fileInput" className="d-block m-0">
                                <Box
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    sx={{
                                        border: `1px dashed ${isDragOver ? '#0dcaf0' : '#495057'}`,
                                        backgroundColor: isDragOver ? 'rgba(13, 202, 240, 0.05)' : 'transparent',
                                        padding: '20px 14px',
                                        textAlign: 'center',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        borderRadius: '8px',
                                        color: '#0dcaf0'
                                    }}
                                >
                                    {isUploading ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
                                            <Spinner size={30} color="#0dcaf0" />
                                            <span style={{ fontSize: '14px' }}>Uploading...</span>
                                        </Box>
                                    ) : (
                                        <>
                                            <LuUpload size={18} style={{ marginRight: '8px' }} />
                                            <span>Click to upload or drag and drop</span><br />
                                            <span style={{ fontSize: '12px', color: '#6c757d' }}>PDF, JPG, PNG , DOCS, XLSX, PPTX (Max 30MB)</span>
                                        </>
                                    )}
                                </Box>
                            </label>
                            <input type="file" id="fileInput" style={{ display: 'none' }} onChange={handleFileUpload} />
                            {selectedEntry?.attachment && selectedEntry.attachment.length > 0 && (
                                <Box mt={2}>
                                    <span style={{ fontSize: '14px', fontWeight: 500, color: titleHeader, marginBottom: '8px', display: 'block' }}>
                                        Preview:
                                    </span>
                                    <Box sx={{ maxHeight: '150px', overflowY: 'auto', paddingRight: '4px' }}>
                                        {selectedEntry.attachment.map((url: string, index: number) => {
                                            const parts = url.split('/');
                                            const filename = parts[parts.length - 1];
                                            const cdnUrl = process.env.REACT_APP_CDN_URL || '';
                                            const fullUrl = `${cdnUrl}${url}`;
                                            return (
                                                <div key={index} style={{ fontSize: '13px', color: '#0dcaf0', marginTop: index === 0 ? '0px' : '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(13, 202, 240, 0.1)', padding: '6px 12px', borderRadius: '4px' }}>
                                                    <a href={fullUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                                        {filename}
                                                    </a>
                                                    <LuTrash2
                                                        style={{ cursor: 'pointer', color: '#dc3545', flexShrink: 0, marginLeft: '8px' }}
                                                        size={16}
                                                        onClick={() => removeAttachment(index)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                        <Box mb={3}>
                            <FormSelect
                                label="Stamped As"
                                placeholder='Select Stamped As'
                                sx={{ color: titleHeader }}
                                menuPlacement='top'
                                value={selectedEntry?.stampedAs || ''}
                                options={caPhaseStampedAsOptions}
                                onChange={(opt: { label: string; value: string }) => setSelectedEntry(prev => prev ? { ...prev, stampedAs: opt.value } : null)}
                            />
                        </Box>
                    </>
                )}
            </ModalBody>
            <ModalFooter style={{ backgroundColor: modalHeader }}>
                <Button variant="CancelButton" className='me-2' onClick={toggle} disabled={isLoading}>Cancel</Button>
                <Button
                    variant="SaveButton"
                    onClick={handleSave}
                    disabled={isLoading || isUploading || disableSave || isRestricted}
                    sx={{
                        opacity: (isLoading || isUploading || disableSave || isRestricted) ? 0.6 : 1,
                        cursor: (isLoading || isUploading || disableSave || isRestricted) ? 'not-allowed' : 'pointer'
                    }}
                >
                    {isLoading ? <Spinner size={20} color="white" /> : 'Save'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default HistoryEntryModal;
